// src/common/components/PageMeta/PageMeta.tsx
import { Helmet } from 'react-helmet-async';
import { usePageMeta } from './usePageMeta';

interface PageMetaProps {
  itemName?: string;
  itemDescription?: string;
  itemImageFile?: string;
  itemPrice?: number;
  itemId?: number;
  customTitle?: string;
  noIndex?: boolean;
  type?: 'website' | 'product' | 'article' | 'place';
}

export const PageMeta: React.FC<PageMetaProps> = (props) => {
  const meta = usePageMeta(props);

  // Генерация JSON-LD для Schema.org
  const generateProductJsonLd = () => {
    if (meta.type !== 'product' || !meta.price) return null;

    const jsonLd: any = {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: props.itemName || (() => {
        const parts = meta.title.split(' | ');
        return parts[0] || meta.title;
      })(),
      description: meta.description,
      image: meta.image,
      sku: props.itemId ? String(props.itemId) : undefined,
      offers: {
        '@type': 'Offer',
        price: meta.price,
        priceCurrency: 'RUB',
        url: meta.url,
      },
    };

    // Чистим undefined поля
    Object.keys(jsonLd).forEach((key) => {
      if (jsonLd[key] === undefined) delete jsonLd[key];
    });
    if (jsonLd.offers) {
      Object.keys(jsonLd.offers).forEach((key) => {
        if (jsonLd.offers[key] === undefined) delete jsonLd.offers[key];
      });
    }

    return jsonLd;
  };

  const productJsonLd = generateProductJsonLd();

  return (
    <Helmet>
      <title>{meta.title}</title>
      <meta name="description" content={meta.description} />

      {/* Open Graph */}
      <meta property="og:title" content={meta.title} />
      <meta property="og:description" content={meta.description} />
      <meta property="og:image" content={meta.image} />
      <meta property="og:url" content={meta.url} />
      <meta property="og:type" content={meta.type} />
      <meta property="og:site_name" content={meta.siteName} />

      {/* Open Graph для товаров */}
      {meta.type === 'product' && meta.price && (
        <>
          <meta property="product:price:amount" content={String(meta.price)} />
          <meta property="product:price:currency" content="RUB" />
        </>
      )}

      {/* JSON-LD для Schema.org */}
      {productJsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(productJsonLd, null, 0)}
        </script>
      )}

      {/* Запрет индексации */}
      {props.noIndex && <meta name="robots" content="noindex, nofollow" />}

      {/* Canonical */}
      <link rel="canonical" href={meta.url} />
    </Helmet>
  );
};